import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { adminAssetListQuery } from '@xdocs/schemas';
import type { AdminAssetListResult, AdminAssetRow, AdminAssetType, ErrorAlert, ModerationStatus } from '@xdocs/schemas';
import type { DataTableQuery } from '@xdocs/ui';
import { useWs } from '../../../ws/WsProvider.js';

const DEFAULT_QUERY: DataTableQuery = { page: 1, pageSize: 25, sort: 'createdAt', dir: 'desc' };
export const ADMIN_ASSET_LIST_QUERY_KEY_PREFIX = 'admin-asset-list' as const;

export interface AssetFilters {
  type?: AdminAssetType | undefined;
  moderationStatus?: ModerationStatus | undefined;
}

export interface UseAssetsListResult {
  rows: readonly AdminAssetRow[];
  total: number | null;
  nextCursor: string | null;
  query: DataTableQuery;
  onQueryChange: (next: DataTableQuery) => void;
  onLoadMore: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  loadMoreError: string | undefined;
  onRetry: () => void;
  invalidate: () => void;
}

/** Server-side pagination/sort/search/filter over `admin.assets.list` (F60, E4 §8.3). */
export function useAssetsList(filters: AssetFilters = {}): UseAssetsListResult {
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState<DataTableQuery>(DEFAULT_QUERY);
  const [rows, setRows] = useState<AdminAssetRow[]>([]);
  const appendModeRef = useRef(false);
  const enabled = connectionState === 'connected';
  const queryKey = [ADMIN_ASSET_LIST_QUERY_KEY_PREFIX, query, filters.type ?? null, filters.moderationStatus ?? null] as const;

  const result = useQuery<AdminAssetListResult, ErrorAlert>({
    queryKey,
    queryFn: () => requestTyped(wsClient, 'admin.assets.list', adminAssetListQuery.parse({ ...query, ...filters })),
    enabled,
  });

  useEffect(() => {
    if (!result.data) return;
    setRows((prev) => (appendModeRef.current ? [...prev, ...result.data.rows] : result.data.rows));
  }, [result.data]);

  // Filter changes reset to page 1 — mirrors `useExplorerList`'s `folderId`
  // reset. Plain `.ts` file: `react-hooks/exhaustive-deps` isn't registered
  // outside `.jsx`/`.tsx` in this repo's ESLint config.
  useEffect(() => {
    appendModeRef.current = false;
    setQuery((prev) => ({ ...prev, page: 1 }));
  }, [filters.type, filters.moderationStatus]);

  const onQueryChange = useCallback((next: DataTableQuery) => {
    appendModeRef.current = false;
    setQuery(next);
  }, []);

  const onLoadMore = useCallback(() => {
    appendModeRef.current = true;
    setQuery((q) => ({ ...q, page: q.page + 1 }));
  }, []);

  const onRetry = useCallback(() => void result.refetch(), [result]);
  const invalidate = useCallback(
    () => void queryClient.invalidateQueries({ queryKey: [ADMIN_ASSET_LIST_QUERY_KEY_PREFIX] }),
    [queryClient],
  );

  const total = result.data?.total ?? null;
  const hasMore = total !== null ? rows.length < total : false;
  const isAppendFetch = appendModeRef.current && result.isFetching;

  return {
    rows,
    total,
    nextCursor: hasMore ? 'more' : null,
    query,
    onQueryChange,
    onLoadMore,
    isLoading: result.isLoading && !appendModeRef.current,
    isLoadingMore: isAppendFetch,
    isError: result.isError,
    loadMoreError: appendModeRef.current && result.isError ? result.error?.message : undefined,
    onRetry,
    invalidate,
  };
}
